/**
 * RowActionsMenu — per-row overflow actions ("⋮") for DataGrid.
 *
 * Why this exists (Bill 2026-07-15, ahead of the Friday retail walk-through):
 * the CIF Products tab rendered FOUR outlined action buttons on EVERY row —
 * 44 identical pills in one viewport for an 11-account customer. Every row
 * shouted equally, so nothing read as important, and the grid stopped looking
 * like the rest of the app. Bill's verdict on the screen: "Yuck."
 *
 * One trigger per row, actions on demand. The account number and type — the
 * things an officer actually scans for — get the horizontal space back.
 *
 * Deliberately NOT a general popover library:
 *   · closes on outside click, Escape, scroll, and after any action fires
 *   · renders inline (no portal) — DataGrid rows don't clip, and a portal would
 *     need scroll-position tracking for no benefit here
 *   · right-aligned, since it lives in the last column
 *
 * Disabled items stay VISIBLE rather than hidden: an officer who can't do
 * something should see that it exists and is unavailable, not wonder whether
 * the app forgot it. `disabledReason` renders as the title so the "why" is one
 * hover away — the same instinct as fail-closed errors that say what to do.
 */
import { useCallback, useEffect, useId, useRef, useState, type ReactNode } from 'react';

export interface RowAction {
  /** Stable key — also the a11y id suffix. */
  key: string;
  label: string;
  onClick: () => void;
  disabled?: boolean;
  /** Shown on hover when disabled. Say what would make it available. */
  disabledReason?: string;
  /** Renders in the danger colour. Destructive actions only. */
  danger?: boolean;
}

export interface RowActionsMenuProps {
  actions: RowAction[];
  /** Accessible label for the trigger, e.g. "Actions for account 666039298". */
  ariaLabel: string;
  /** Rendered when every action is filtered out. Default: nothing. */
  emptyFallback?: ReactNode;
}

export function RowActionsMenu({ actions, ariaLabel, emptyFallback = null }: RowActionsMenuProps): JSX.Element | null {
  const [open, setOpen] = useState(false);
  const wrapRef = useRef<HTMLDivElement | null>(null);
  const menuId = useId();

  const close = useCallback(() => setOpen(false), []);

  useEffect(() => {
    if (!open) return undefined;
    const onDocClick = (e: MouseEvent): void => {
      if (wrapRef.current && !wrapRef.current.contains(e.target as Node)) close();
    };
    const onKey = (e: KeyboardEvent): void => {
      if (e.key === 'Escape') close();
    };
    // Capture-phase scroll: a menu anchored to a row must not float away from it.
    document.addEventListener('mousedown', onDocClick);
    document.addEventListener('keydown', onKey);
    window.addEventListener('scroll', close, true);
    return () => {
      document.removeEventListener('mousedown', onDocClick);
      document.removeEventListener('keydown', onKey);
      window.removeEventListener('scroll', close, true);
    };
  }, [open, close]);

  if (actions.length === 0) return <>{emptyFallback}</>;

  return (
    <div ref={wrapRef} style={{ position: 'relative', display: 'flex', justifyContent: 'flex-end' }}>
      <button
        type="button"
        aria-label={ariaLabel}
        aria-haspopup="menu"
        aria-expanded={open}
        aria-controls={open ? menuId : undefined}
        onClick={() => setOpen((v) => !v)}
        style={{
          display: 'inline-flex',
          alignItems: 'center',
          justifyContent: 'center',
          width: 28,
          height: 28,
          padding: 0,
          border: '1px solid transparent',
          borderRadius: 6,
          background: open ? 'var(--pcb-surface-2, #eef2f7)' : 'transparent',
          color: 'var(--pcb-text-muted, #5b6b7c)',
          cursor: 'pointer',
          fontSize: 16,
          lineHeight: 1,
        }}
      >
        {/* Vertical ellipsis — the conventional overflow affordance. */}
        <span aria-hidden="true">⋮</span>
      </button>

      {open && (
        <div
          id={menuId}
          role="menu"
          style={{
            position: 'absolute',
            top: 30,
            right: 0,
            zIndex: 20,
            minWidth: 190,
            padding: 4,
            background: 'var(--pcb-surface, #fff)',
            border: '1px solid var(--pcb-border, #d9e1ea)',
            borderRadius: 8,
            boxShadow: '0 6px 20px rgba(16, 32, 48, 0.14)',
          }}
        >
          {actions.map((a) => (
            <button
              key={a.key}
              type="button"
              role="menuitem"
              disabled={a.disabled === true}
              title={a.disabled === true ? a.disabledReason : undefined}
              onClick={() => {
                // Close first: every action opens a dialog, and a menu still
                // hanging over a modal is exactly the kind of thing that reads
                // as broken in a walk-through.
                close();
                a.onClick();
              }}
              style={{
                display: 'block',
                width: '100%',
                padding: '7px 10px',
                border: 'none',
                borderRadius: 6,
                background: 'transparent',
                textAlign: 'left',
                fontSize: 13,
                cursor: a.disabled === true ? 'not-allowed' : 'pointer',
                color: a.disabled === true
                  ? 'var(--pcb-text-disabled, #9aa8b6)'
                  : a.danger === true
                    ? 'var(--pcb-danger, #b10505)'
                    : 'var(--pcb-text, #1f2d3a)',
              }}
              onMouseEnter={(e) => {
                if (a.disabled !== true) e.currentTarget.style.background = 'var(--pcb-surface-2, #eef2f7)';
              }}
              onMouseLeave={(e) => { e.currentTarget.style.background = 'transparent'; }}
            >
              {a.label}
            </button>
          ))}
        </div>
      )}
    </div>
  );
}
